// ─── Agent status (traffic-light) ─────────────────────────────────────────────
//
// Derives a single green / amber / red / idle status for the at-a-glance agent
// grid from the live telemetry returned by /api/v1/agents. Pure so the rules
// can be unit-tested.

import type { AgentSummary } from './api'

export type AgentStatus = 'green' | 'amber' | 'red' | 'idle'

/** An agent that hasn't run within this window (but claims to be active) is stale. */
export const AGENT_STALE_MS = 24 * 60 * 60 * 1000

/**
 * Map an agent's live telemetry to a traffic-light status.
 *
 *  - red   — runtime error, last run failed, or success rate below 50%
 *  - amber — paused, never run, stale (>24h), or success rate below 80%
 *  - green — active, ran recently, healthy success rate
 *  - idle  — no live telemetry at all (e.g. a non-LLM infrastructure service)
 */
export function computeAgentStatus(live: AgentSummary | undefined, now = Date.now()): AgentStatus {
  if (!live) return 'idle'

  if (live.status === 'error' || live.last_run_outcome === 'failed') return 'red'
  if (live.status === 'paused') return 'amber'

  const rate = live.success_rate_24h
  if (rate !== null && rate !== undefined) {
    if (rate < 50) return 'red'
    if (rate < 80) return 'amber'
  }

  if (live.status === 'active') {
    if (!live.last_run_at) return 'amber'
    if (now - new Date(live.last_run_at).getTime() > AGENT_STALE_MS) return 'amber'
    return 'green'
  }

  // Some other status string but telemetry exists — treat presence of a recent
  // run as healthy, otherwise idle.
  return live.last_run_at ? 'green' : 'idle'
}

export interface AgentStatusMeta {
  /** Tailwind background class for the status dot. */
  dot: string
  /** Short uppercase label (also used as the screen-reader text). */
  label: string
}

const META: Record<AgentStatus, AgentStatusMeta> = {
  green: { dot: 'bg-[#80ffb4]', label: 'HEALTHY' },
  amber: { dot: 'bg-[#ffd080]', label: 'ATTENTION' },
  red:   { dot: 'bg-cmd-error', label: 'FAULT' },
  idle:  { dot: 'bg-cmd-outline-variant', label: 'IDLE' },
}

export function agentStatusMeta(status: AgentStatus): AgentStatusMeta {
  return META[status]
}

export interface AgentStatusCounts {
  green: number
  amber: number
  red: number
  idle: number
  total: number
}

export function countStatuses(statuses: AgentStatus[]): AgentStatusCounts {
  const counts: AgentStatusCounts = { green: 0, amber: 0, red: 0, idle: 0, total: statuses.length }
  for (const s of statuses) counts[s] += 1
  return counts
}
